/**
 * Recursively locate a folder or document for notification deep-linking.
 */

import { extractUserFilesList } from "./documentTabUnread";

const folderCategoryParams = (documentCategory) =>
  documentCategory
    ? { document_category: documentCategory }
    : {};

export async function searchFolderRecursively({
  dispatch,
  documentAction,
  targetFolderId,
  searchUserId,
  searchHomeFolder,
  documentCategory,
  currentPath = [{ id: 0, name: "Home", created_by: 0 }],
  visitedFolders = new Set(),
}) {
  const currentFolderId = currentPath[currentPath.length - 1].id;
  const currentFolderName =
    currentPath[currentPath.length - 1].name === "Home"
      ? searchHomeFolder
      : currentPath[currentPath.length - 1].name;

  const visitKey = `${searchUserId}-${currentFolderId}`;
  if (visitedFolders.has(visitKey)) return null;
  visitedFolders.add(visitKey);

  try {
    const folderResponse = await dispatch(
      documentAction.getUserFolders({
        user_id: searchUserId,
        folder_name: currentFolderName,
        folder_id: currentFolderId,
        ...folderCategoryParams(documentCategory),
      }),
    );

    const folders = folderResponse.payload || [];
    const foundFolder = folders.find(
      (f) => String(f.client_folder_id) === String(targetFolderId),
    );

    if (foundFolder) {
      let folderNavPath = [...currentPath];
      if (foundFolder.directory_details) {
        try {
          folderNavPath =
            typeof foundFolder.directory_details === "string"
              ? JSON.parse(foundFolder.directory_details)
              : foundFolder.directory_details;
        } catch {
          folderNavPath.push({
            id: foundFolder.client_folder_id,
            name: foundFolder.folder_name,
            created_by: foundFolder.created_by || 0,
            folder_type: foundFolder.folder_type,
          });
        }
      } else {
        folderNavPath.push({
          id: foundFolder.client_folder_id,
          name: foundFolder.folder_name,
          created_by: foundFolder.created_by || 0,
          folder_type: foundFolder.folder_type,
        });
      }
      return { folderNavPath, clientId: searchUserId };
    }

    for (const folder of folders) {
      const subPath = [
        ...currentPath,
        {
          id: folder.client_folder_id,
          name: folder.folder_name,
          created_by: folder.created_by || 0,
          folder_type: folder.folder_type,
        },
      ];
      const result = await searchFolderRecursively({
        dispatch,
        documentAction,
        targetFolderId,
        searchUserId,
        searchHomeFolder,
        documentCategory,
        currentPath: subPath,
        visitedFolders,
      });
      if (result) return result;
    }
  } catch (error) {
    console.error("Error searching folder:", error);
  }

  return null;
}

export async function searchDocumentRecursively({
  dispatch,
  documentAction,
  targetDocumentId,
  searchUserId,
  searchHomeFolder,
  documentCategory,
  currentPath = [{ id: 0, name: "Home", created_by: 0 }],
  visitedFolders = new Set(),
}) {
  const currentFolderId = currentPath[currentPath.length - 1].id;
  const currentFolderName =
    currentPath[currentPath.length - 1].name === "Home"
      ? searchHomeFolder
      : currentPath[currentPath.length - 1].name;

  const visitKey = `${searchUserId}-${currentFolderId}`;
  if (visitedFolders.has(visitKey)) return null;
  visitedFolders.add(visitKey);

  try {
    const fileResponse = await dispatch(
      documentAction.getUserFiles({
        user_id: searchUserId,
        folder_name: currentFolderName,
        folder_id: currentFolderId,
        ...folderCategoryParams(documentCategory),
      }),
    );

    const files = extractUserFilesList(fileResponse.payload);
    const foundDocument = files.find(
      (f) => String(f.client_document_id) === String(targetDocumentId),
    );
    if (foundDocument) {
      return { folderNavPath: currentPath, clientId: searchUserId };
    }

    const folderResponse = await dispatch(
      documentAction.getUserFolders({
        user_id: searchUserId,
        folder_name: currentFolderName,
        folder_id: currentFolderId,
        ...folderCategoryParams(documentCategory),
      }),
    );

    const folders = folderResponse.payload || [];
    for (const folder of folders) {
      const subPath = [
        ...currentPath,
        {
          id: folder.client_folder_id,
          name: folder.folder_name,
          created_by: folder.created_by || 0,
          folder_type: folder.folder_type,
        },
      ];
      const result = await searchDocumentRecursively({
        dispatch,
        documentAction,
        targetDocumentId,
        searchUserId,
        searchHomeFolder,
        documentCategory,
        currentPath: subPath,
        visitedFolders,
      });
      if (result) return result;
    }
  } catch (error) {
    console.error("Error searching document:", error);
  }

  return null;
}
